// frontend/src/pages/admin/Tournaments.jsx
import React, { useState, useEffect } from 'react';
import axios from '../../config/axios';
import { Plus, AlertTriangle, Trash2 } from 'lucide-react';
import CreateTournamentModal from '../../components/admin/modals/CreateTournamentModal';
import CancelTournamentModal from '../../components/admin/modals/CancelTournamentModal';
import TournamentDetailsModal from '../../components/admin/modals/TournamentDetailsModal';
import ClearTournamentsModal from '../../components/admin/modals/ClearTournamentsModal';

const Tournaments = () => {
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedTournament, setSelectedTournament] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedTournamentForDetails, setSelectedTournamentForDetails] = useState(null);
    const [showClearModal, setShowClearModal] = useState(false);

    const fetchTournaments = async () => {
        try {
            const response = await axios.get('/admin/tournaments');
            if (response.data.success) {
                setTournaments(response.data.tournaments);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al cargar torneos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTournaments();
    }, []);

    const handleCancelTournament = (tournament) => {
        setSelectedTournament(tournament);
        setShowCancelModal(true);
    };

    const formatDateTime = (date) => {
        return new Date(date).toLocaleString('es-AR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const styles = {
            REGISTERING: 'bg-blue-900 text-blue-300',
            IN_PROGRESS: 'bg-green-900 text-green-300',
            COMPLETED: 'bg-gray-700 text-gray-300'
        };
        const labels = {
            REGISTERING: 'Registro Abierto',
            IN_PROGRESS: 'En Curso',
            COMPLETED: 'Finalizado'
        };
        return (
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${styles[status] || styles.COMPLETED}`}>
                {labels[status] || status}
            </span>
        );
    };

    if (loading) {
        return <div className="flex justify-center items-center h-full text-gray-400">Cargando...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Gestión de Torneos</h1>
                <div className="flex space-x-3">
                    <button
                        onClick={() => setShowClearModal(true)}
                        className="inline-flex items-center px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-md transition-colors"
                    >
                        <Trash2 className="w-5 h-5 mr-2" />
                        Limpiar Torneos
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="inline-flex items-center px-4 py-2 text-white rounded-md transition-colors"
                        style={{ backgroundColor: '#2284DF' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1a6ec0'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2284DF'}
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Crear Torneo
                    </button>
                </div>
            </div>

            {error && (
                <div className="p-4 rounded-lg flex items-center gap-2" style={{ backgroundColor: '#2d1a1a' }}>
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <p className="text-red-400">{error}</p>
                </div>
            )}

            <div className="rounded-lg overflow-hidden" style={{ backgroundColor: '#0E2238' }}>
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead style={{ backgroundColor: '#0A1929' }}>
                            <tr>
                                {['Torneo', 'Juego', 'Participantes', 'Entrada', 'Inicio', 'Estado', 'Acciones'].map(col => (
                                    <th key={col} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400">
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tournaments.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                        No hay torneos disponibles
                                    </td>
                                </tr>
                            ) : (
                                tournaments.map((tournament) => (
                                    <tr key={tournament.id} className="border-t" style={{ borderColor: '#122436' }}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    setSelectedTournamentForDetails(tournament.id);
                                                    setShowDetailsModal(true);
                                                }}
                                                className="text-sm font-medium transition-colors"
                                                style={{ color: '#2284DF' }}
                                                onMouseEnter={e => e.currentTarget.style.color = '#FCE503'}
                                                onMouseLeave={e => e.currentTarget.style.color = '#2284DF'}
                                            >
                                                {tournament.name}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                            {tournament.game.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                            {tournament._count.players}/{tournament.maxPlayers}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                            {tournament.entryFee} tickets
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                            {formatDateTime(tournament.startTime)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(tournament.status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => handleCancelTournament(tournament)}
                                                className="text-red-400 hover:text-red-300 transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCreateModal && (
                <CreateTournamentModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => { setShowCreateModal(false); fetchTournaments(); }}
                />
            )}

            {showCancelModal && selectedTournament && (
                <CancelTournamentModal
                    tournament={selectedTournament}
                    onClose={() => { setShowCancelModal(false); setSelectedTournament(null); }}
                    onSuccess={() => { setShowCancelModal(false); setSelectedTournament(null); fetchTournaments(); }}
                />
            )}

            {showDetailsModal && selectedTournamentForDetails && (
                <TournamentDetailsModal
                    tournamentId={selectedTournamentForDetails}
                    onClose={() => { setShowDetailsModal(false); setSelectedTournamentForDetails(null); }}
                />
            )}

            {showClearModal && (
                <ClearTournamentsModal
                    onClose={() => setShowClearModal(false)}
                    onSuccess={() => { setShowClearModal(false); fetchTournaments(); }}
                />
            )}
        </div>
    );
};

export default Tournaments;
